import * as React from 'react'

import { Alert, Box, Button, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import { DocumentTemplateDetailsQuery } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import Popover from 'modules/core/components/Popover'
import PopoverContent from 'modules/core/components/Popover/Content'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useEffect } from 'modules/core/hooks/useEffect'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import useDeleteDocumentTemplate from 'modules/documents/hooks/useDeleteDocumentTemplate'
import type { DocumentTemplate } from 'modules/documents/hooks/useGetDocumentTemplates'

type PopoverContentProps = React.ComponentProps<typeof PopoverContent>

type TaskConfigurationSendDocumentTemplate =
  DocumentTemplateDetailsQuery['processes']['taskConfigurationSendDocumentTemplatesConnection']['nodes'][number]

interface Props {
  documentTemplate: DocumentTemplate
  onDelete?: () => void
  taskConfigurations?: TaskConfigurationSendDocumentTemplate[]
}

const renderAlertMessage = (type: 'warning' | 'error', children: React.ReactNode) => (
  <Alert color={type}>
    <Box gap="s4">
      <Text.Normal>{children}</Text.Normal>
    </Box>
  </Alert>
)

const SettingsButton = ({ documentTemplate, onDelete, taskConfigurations }: Props) => {
  const [isOpen, toggleOpen] = useToggle(false)
  const [openTooltip, toggleTooltip] = useToggle(false)
  const [open, toggle] = useToggle(false)
  const { deleteDocumentTemplate, loading } = useDeleteDocumentTemplate()
  const [error, setError] = React.useState('')

  const options: PopoverContentProps['options'] = [
    {
      title: i18n.t('documents.document_templates.list.item.delete'),
      onClick: toggleOpen(true),
      type: 'wrong',
    },
  ]

  useEffect(() => {
    if (loading) {
      app.setProgress({ message: i18n.t('common.deleting') })
    } else {
      app.setProgress(null)
    }
  }, [loading])

  const { running: deleting, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      setError('')

      if (!documentTemplate.id) return

      const result = await deleteDocumentTemplate({ id: documentTemplate.id })

      const handled = handleResult(result, (errors) => {
        const errorMessage = errors?.base || i18n.t('errors.generic_error')

        setError(errorMessage)
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        closePortal()

        if (onDelete) onDelete()
      }
    },
    {
      successFlash: false,
    }
  )

  const popoverButton = (
    <Button
      disabled={documentTemplate.systemTemplate}
      type="secondary"
      onClick={toggle(true)}
      aria-label={i18n.t('documents.document_templates.list.delete_template')}
      iconName="ellipsis"
    />
  )

  const disableDelete = deleting || !!taskConfigurations?.length || !!error

  return (
    <Box>
      <Popover attachment="top right" targetAttachment="bottom center" toggle={toggle} open={open}>
        {documentTemplate.systemTemplate ? (
          <Tooltip.Dialog
            attachment="bottom center"
            open={openTooltip}
            toggle={toggleTooltip}
            label={i18n.t('documents.document_templates.list.delete_not_allowed')}
          >
            {popoverButton}
          </Tooltip.Dialog>
        ) : (
          popoverButton
        )}

        <PopoverContent onClose={toggle(false)} options={options} />
      </Popover>
      {isOpen && (
        <Modal
          title={i18n.t('documents.document_templates.list.delete_modal.title')}
          description={i18n.t('documents.document_templates.list.delete_modal.description_v2', {
            template: documentTemplate.name,
          })}
          illustration="delete"
          onClose={toggleOpen(false)}
          onSubmit={onSubmit}
          footer={
            <>
              <Button onClick={toggleOpen(false)} type="secondary">
                {i18n.t('common.cancel')}
              </Button>

              <Button disabled={disableDelete} submit>
                {i18n.t('documents.document_templates.list.item.delete')}
              </Button>
            </>
          }
        >
          {!!taskConfigurations?.length &&
            renderAlertMessage(
              'warning',
              <>
                {i18n.t('documents.document_templates.list.delete_modal.workflows_alert')}
                {taskConfigurations
                  .map((taskConfiguration) => `"${taskConfiguration.title}"`)
                  .join(', ')}
              </>
            )}

          {!!error && renderAlertMessage('error', error)}
        </Modal>
      )}
    </Box>
  )
}

export default observer(SettingsButton)
